import { hasBannedCharacters } from '../../lib/validation/text_validation';

type ValidatorFn = (value: unknown) => boolean;

interface ValidationRule {
  validate: ValidatorFn;
  message: string;
  is_optional?: boolean;
}

// Keys are dot-notation paths (e.g. "metadata.group_id").
type ValidationSchema = Record<string, ValidationRule>;

// Utility to resolve "metadata.group_id" into params.metadata.group_id.
export const getValueByPath = <TObj, TValue = unknown>(
  obj: TObj,
  path: string
): TValue | undefined => {
  return path.split('.').reduce((acc: unknown, part) => {
    if (acc && typeof acc === 'object' && part in acc) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj) as TValue | undefined;
};

/**
 * Validates an object against a validation schema.
 * @template T Type of the object to validate.
 * @param {T} params The object containing values to validate.
 * @param {ValidationSchema} schema Validation rules keyed by object path.
 * @returns {params is T & Record<string, string>} Returns true if validation passes, otherwise throws an error.
 * @throws {Error} If a validation rule fails.
 */
export const validateParams = <T>(
  params: T,
  schema: ValidationSchema
): params is T & Record<string, string> => {
  for (const path in schema) {
    const rule: ValidationRule = schema[path];
    const value = getValueByPath(params, path);
    if (!value && rule.is_optional) continue;
    if (!rule.validate(value)) {
      throw new Error(rule.message);
    }
  }
  return true;
};

/**
 * Validates a generated firestore ID string by ensuring it is non-empty and does not contain banned characters.
 * @param {string} id The firestore ID string to validate.
 * @returns {boolean} `true` if the firestore ID is valid, `false` otherwise.
 */
export const isValidFirestoreId = (id: unknown): boolean =>
  typeof id === 'string' &&
  !!(
    id &&
    !hasBannedCharacters(id, {
      banned_characters: "- .()'&$+%@"
    })
  );

/**
 * Validates a field value for being a valid boolean value (true or false only).
 * @param {unknown} value The boolean value to be validated.
 * @returns {boolean}
 */
export const isValidBoolean = (value: unknown): boolean => typeof value === 'boolean';

/**
 * Validates an entity field value for being a valid BaseFirestoreDocument to be used as a reference field in the database.
 * @param {unknown} reference Field value to validate.
 * @returns {boolean}
 */
export const isValidBaseReference = (reference: unknown): boolean => {
  return !!reference && typeof reference === 'object' && 'id' in reference;
};

/**
 * Validates an array of entity field values for being valid BaseFirestoreDocuments to be used as an array of reference fields in the database.
 * @param {Array<unknown>} reference_array Array of field values to validate.
 * @param {boolean | undefined } allow_empty Optionally allow empty arrays, defaults to false.
 * @returns {boolean}
 */
export const isValidBaseReferenceArray = (
  reference_array: unknown,
  allow_empty = false
): boolean => {
  return (
    Array.isArray(reference_array) &&
    (!!reference_array.length || allow_empty) &&
    reference_array.every(isValidBaseReference)
  );
};
